import { Button, Choice, Icon, Tag, type Column, type TagTone } from '@dayon/design-system';
import { formatPesos } from '@dayon/money';
import type { PlayerRow, TransactionRow } from '@dayon/contracts';
import { ResidenceValue } from '../../lib/residence.tsx';
import { initials, relative } from './format.ts';
import {
  KYC_LABEL,
  STATUS_LABEL,
  TIER_LABEL,
  TX_KIND_LABEL,
  kycTone,
  statusTone,
  tierTone,
  type SortDirection,
  type SortKey,
} from './vocabulary.ts';

/**
 * Optional columns.
 *
 * The design's picker offers four and they are on by default. `residence` is a
 * fifth, and it is OFF by default rather than on: it is the newest column, most
 * of a legacy roster answers "never asked" to it, and a column of that on every
 * operator's first visit is noise for the many screens that are not about
 * residency. It is one tick away, and the residency section on Platform
 * settings points at it.
 */
export interface ColumnPrefs {
  agent: boolean;
  kyc: boolean;
  deposits: boolean;
  lastSeen: boolean;
  residence: boolean;
}

const subLine = { fontSize: '11px' };

/**
 * The sort marker a header carries.
 *
 * A glyph in the header STRING rather than a tinted header: the shared
 * DataTable takes a plain string header, and colour alone was never allowed to
 * carry state here anyway. It flips with the direction now — an arrow that
 * always pointed down while the order could only run one way was honest; one
 * that still always pointed down over an ascending sort would not be.
 */
const marker = (active: boolean, direction: SortDirection): string =>
  active ? (direction === 'DESC' ? ' ↓' : ' ↑') : '';

/* ── Columns ───────────────────────────────────────────────────────────────
   A factory, because four cells depend on live state: which rows are selected,
   which optional columns are on, and which header carries the sort arrow. */
export function playerColumns(opts: {
  prefs: ColumnPrefs;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (row: PlayerRow) => void;
  sort: SortKey;
  direction: SortDirection;
  atCap: boolean;
}): Column<PlayerRow>[] {
  const on = (key: SortKey) => marker(opts.sort === key, opts.direction);

  const columns: Column<PlayerRow>[] = [
    {
      key: 'select',
      header: 'Select',
      render: (p) => (
        <Choice
          checked={opts.selected.has(p.id)}
          // The cap is real: past 500 an unselected row cannot be added,
          // because a bulk submission over the cap would be refused whole.
          disabled={opts.atCap && !opts.selected.has(p.id)}
          onChange={() => opts.onToggle(p.id)}
          label={<span className="sr-only">Select {p.name}</span>}
        />
      ),
    },
    {
      key: 'player',
      header: `Player${on('PLAYER')}`,
      render: (p) => (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
          <span aria-hidden="true" className="avatar">
            {initials(p.name)}
          </span>
          <span style={{ minWidth: 0 }}>
            {/* The drawer opens from the NAME, not from the whole row. The row
                also carries a checkbox and a trailing button, and nesting those
                inside a role="button" row is both a mis-click waiting to happen
                and invalid for a screen reader. */}
            <Button variant="ghost" small onClick={() => opts.onOpen(p)}>
              {p.name}
            </Button>
            <div className="text-subtle tabular" style={subLine}>
              {p.ref}
              {/*
                NOT A COLUMN, and not behind the filter either.

                A test account is excluded from Gross Gaming Revenue — PAGCOR
                item 33(c) — and retroactively: marking one retracts its past
                days from months already reported. That is not a property an
                operator should have to turn a column on to discover. A chip
                beside the reference is seen by somebody who came to the roster
                for something else entirely, which is exactly when it matters.
              */}
              {p.isTest && (
                <>
                  {' '}
                  <Tag tone="outline">Test account</Tag>
                </>
              )}
            </div>
          </span>
        </div>
      ),
    },
    {
      key: 'brand',
      header: 'Brand',
      render: (p) => <span className="text-muted">{p.brandName}</span>,
    },
  ];

  if (opts.prefs.agent) {
    columns.push({
      key: 'agent',
      header: 'Agent',
      secondary: true,
      // Denormalised varchar on player.player. agent.agent_player is the
      // authority, but the list index is on this column, so this is what the
      // route reads and what the search matches.
      render: (p) => <span className="text-muted tabular">{p.agentRef ?? '—'}</span>,
    });
  }

  columns.push({
    key: 'tier',
    header: 'Tier',
    render: (p) => <Tag tone={tierTone(p.tier)}>{TIER_LABEL[p.tier] ?? p.tier}</Tag>,
  });

  if (opts.prefs.kyc) {
    columns.push({
      key: 'kyc',
      header: 'KYC',
      render: (p) => <Tag tone={kycTone(p.kycStatus)}>{KYC_LABEL[p.kycStatus] ?? p.kycStatus}</Tag>,
    });
  }

  columns.push(
    {
      key: 'status',
      header: 'Status',
      render: (p) => <Tag tone={statusTone(p.status)}>{STATUS_LABEL[p.status] ?? p.status}</Tag>,
    },
    {
      key: 'balance',
      header: `Balance${on('BALANCE')}`,
      numeric: true,
      // Cash only — wallet.player_cash_balance() reads the PLAYER_CASH account.
      // Bonus and locked funds are a different account kind and are not here.
      // sort=BALANCE orders by that FUNCTION rather than by the text-cast
      // `cash_balance` alias, so the order is numeric.
      render: (p) => <span className="tabular">{formatPesos(BigInt(p.cashBalance))}</span>,
    },
  );

  if (opts.prefs.deposits) {
    columns.push({
      key: 'deposits',
      header: `Lifetime deposits${on('DEPOSITS')}`,
      numeric: true,
      // sum(payment.deposit) where status = SETTLED, computed by the list SQL.
      // Lifetime, not the period the dashboard reports on.
      render: (p) => <span className="tabular">{formatPesos(BigInt(p.lifetimeDeposits))}</span>,
    });
  }

  if (opts.prefs.residence) {
    columns.push({
      key: 'residence',
      header: 'Residence',
      secondary: true,
      /* NOT sortable, and the header carries no marker for it: `/api/players`
         has no residence order and the closed sort map is what makes an ORDER
         BY safe here. A header that offered a sort the route cannot serve would
         reorder one page and misrepresent every other. */
      render: (p) => <ResidenceValue code={p.residenceCountry} />,
    });
  }

  if (opts.prefs.lastSeen) {
    columns.push({
      key: 'lastSeen',
      header: `Last seen${on('LAST_SEEN')}`,
      numeric: true,
      render: (p) =>
        p.lastSeenAt ? (
          <span className="text-muted tabular">{relative(p.lastSeenAt)}</span>
        ) : (
          <span className="text-subtle">never</span>
        ),
    });
  }

  columns.push({
    key: 'open',
    header: 'Open',
    render: (p) => (
      <Button
        iconOnly
        variant="ghost"
        // The design labels this "Open player profile", but it opens the quick
        // look, not the profile. The label says what the button does.
        aria-label={`Quick look at ${p.name}`}
        onClick={() => opts.onOpen(p)}
      >
        <Icon name="arrow-right" />
      </Button>
    ),
  });

  return columns;
}

/* ── Drawer transaction columns ────────────────────────────────────────────
   The design's four columns are Type / Rail / Amount / When. Rail is not on
   this route — /api/transactions never joins payment.payment_method — so Status
   takes its place rather than a column of dashes that would read as "no rail".
   Amount is signed against the PLAYER's balance, which is the sign an operator
   expects: a deposit credits. */
const txStatusTone = (status: string): TagTone =>
  status === 'SETTLED' ? 'neutral' : status === 'FAILED' ? 'quiet' : 'outline';

export const TX_COLUMNS: Column<TransactionRow>[] = [
  {
    key: 'kind',
    header: 'Type',
    render: (t) => TX_KIND_LABEL[t.kind] ?? t.kind,
  },
  {
    key: 'status',
    header: 'Status',
    render: (t) => <Tag tone={txStatusTone(t.status)}>{t.status.toLowerCase().replace('_', ' ')}</Tag>,
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (t) => <span className="tabular">{formatPesos(BigInt(t.amount), { sign: true })}</span>,
  },
  {
    key: 'when',
    header: 'When',
    numeric: true,
    render: (t) => <span className="text-muted tabular">{relative(t.occurredAt)}</span>,
  },
];
